import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { motion, AnimatePresence } from "framer-motion";
import { UploadCloud, X, FileText, AlertCircle, Loader2, MessageCircle, Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/common/SEO";
import PageHero from "@/components/common/PageHero";
import { CTAButton } from "@/components/common/CTA";
import HsCodeField from "@/components/common/HsCodeField";
import api from "@/lib/api";
import {
  serviceOptions, countryOptions, incotermOptions, unitOptions, contactMethods, timelineOptions,
  contact, waLink,
} from "@/config/site";

const ALLOWED = [".pdf", ".docx", ".xlsx", ".jpg", ".jpeg", ".png"];
const MAX_BYTES = 10 * 1024 * 1024;

const Label = ({ children, required, htmlFor }) => (
  <label htmlFor={htmlFor} className="block text-[13px] font-semibold text-navy mb-1.5">
    {children} {required && <span className="text-[#B42318]">*</span>}
  </label>
);

const fieldCls =
  "w-full min-h-[48px] rounded-md border border-[#D9E0E4] bg-white px-3.5 text-[15px] text-navy " +
  "placeholder:text-[#9AA6AE] focus:outline-none focus:ring-2 focus:ring-teal focus:border-teal transition-colors";

const Err = ({ msg }) => msg ? (
  <p className="mt-1 text-[12.5px] text-[#B42318] flex items-center gap-1" role="alert">
    <AlertCircle className="h-3.5 w-3.5" /> {msg}
  </p>
) : null;

export default function RequestQuote() {
  const navigate = useNavigate();
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm({ mode: "onBlur" });
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [pendingData, setPendingData] = useState(null);
  const fileInputRef = useRef(null);

  const handleFiles = async (fileList) => {
    const incoming = Array.from(fileList);
    for (const f of incoming) {
      const ext = "." + f.name.split(".").pop().toLowerCase();
      if (!ALLOWED.includes(ext)) {
        toast.error(`${f.name}: file type not allowed. Use PDF, DOCX, XLSX, JPG or PNG.`);
        continue;
      }
      if (f.size > MAX_BYTES) {
        toast.error(`${f.name}: exceeds 10MB limit.`);
        continue;
      }
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append("file", f);
        const res = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((prev) => [...prev, res.data]);
      } catch (e) {
        toast.error(`${f.name}: upload failed. ${e.response?.data?.detail || ""}`);
      } finally {
        setUploading(false);
      }
    }
  };

  const removeFile = (stored) => setFiles((prev) => prev.filter((f) => f.stored !== stored));

  const onSubmit = async (data) => {
    // Honeypot spam protection
    if (data._hp) return;
    setSubmitError(null);
    setSubmitting(true);
    setPendingData(data);
    try {
      const payload = {
        full_name: data.full_name,
        company_name: data.company_name,
        work_email: data.work_email,
        phone: data.phone,
        country: data.country,
        preferred_contact: data.preferred_contact,
        service: data.service,
        product_name: data.product_name,
        product_description: data.product_description,
        specifications: data.specifications,
        hs_code: data.hs_code,
        unit: data.unit,
        quantity: data.quantity,
        sourcing_country: data.sourcing_country,
        delivery_destination: data.delivery_destination,
        timeline: data.timeline,
        budget: data.budget,
        incoterm: data.incoterm,
        message: data.message,
        consent: !!data.consent,
        attachments: files.map((f) => ({ name: f.name, url: f.url, stored: f.stored })),
      };
      const res = await api.post("/quotes", payload);
      navigate("/thank-you", { state: { reference: res.data.reference, name: res.data.full_name } });
    } catch (e) {
      setSubmitError(e.response?.data?.detail || "Something went wrong while submitting your enquiry. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Request a Sourcing Quote | Premier Global Sourcing"
        description="Request a sourcing quote from Premier Global Sourcing. Share your product, specifications, quantity and destination and our international team will review it."
        path="/request-a-quote"
      />
      <PageHero
        eyebrow="Request a quote"
        title="Tell us what you need"
        intro="Complete the sections below with as much detail as you can. Required fields are marked. Our team will review the requirement and respond with the next steps."
        breadcrumbs={[{ label: "Request a Quote" }]}
      />

      <section className="container-page py-16 lg:py-24 grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <form onSubmit={handleSubmit(onSubmit)} noValidate data-testid="request-quote-form" className="space-y-12">
            {/* Honeypot */}
            <input type="text" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" {...register("_hp")} />

            {/* Section 1 */}
            <fieldset>
              <legend className="font-heading font-bold text-[20px] text-navy mb-1">1. Your contact details</legend>
              <p className="text-[14px] text-muted-foreground mb-6">So we know who to respond to.</p>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="full_name" required>Full name</Label>
                  <input id="full_name" className={fieldCls} data-testid="qf-full-name"
                    {...register("full_name", { required: "Please enter your full name." })} />
                  <Err msg={errors.full_name?.message} />
                </div>
                <div>
                  <Label htmlFor="company_name">Company name</Label>
                  <input id="company_name" className={fieldCls} data-testid="qf-company" {...register("company_name")} />
                </div>
                <div>
                  <Label htmlFor="work_email" required>Work email</Label>
                  <input id="work_email" type="email" className={fieldCls} data-testid="qf-email"
                    {...register("work_email", {
                      required: "Please enter your email.",
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Enter a valid email address." },
                    })} />
                  <Err msg={errors.work_email?.message} />
                </div>
                <div>
                  <Label htmlFor="phone" required>Phone / WhatsApp</Label>
                  <input id="phone" className={fieldCls} data-testid="qf-phone"
                    {...register("phone", { required: "Please enter a phone or WhatsApp number." })} />
                  <Err msg={errors.phone?.message} />
                </div>
                <div>
                  <Label htmlFor="country">Country</Label>
                  <select id="country" className={fieldCls} data-testid="qf-country" defaultValue="" {...register("country")}>
                    <option value="" disabled>Select country</option>
                    {countryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <Label htmlFor="preferred_contact">Preferred contact method</Label>
                  <select id="preferred_contact" className={fieldCls} data-testid="qf-contact-method" defaultValue="" {...register("preferred_contact")}>
                    <option value="" disabled>Select method</option>
                    {contactMethods.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
            </fieldset>

            {/* Section 2 */}
            <fieldset>
              <legend className="font-heading font-bold text-[20px] text-navy mb-1">2. Your requirement</legend>
              <p className="text-[14px] text-muted-foreground mb-6">The more detail you provide, the faster we can review it.</p>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="service">Required service</Label>
                  <select id="service" className={fieldCls} data-testid="qf-service" defaultValue="" {...register("service")}>
                    <option value="" disabled>Select a service</option>
                    {serviceOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <Label htmlFor="product_name">Product name</Label>
                  <input id="product_name" className={fieldCls} data-testid="qf-product-name" {...register("product_name")} />
                </div>
                <div>
                  <Label htmlFor="hs_code">HS Code <span className="text-muted-foreground font-normal">(type code or description)</span></Label>
                  <HsCodeField
                    value={watch("hs_code") || ""}
                    onChange={(v) => setValue("hs_code", v)}
                    onSelect={(s) => { setValue("hs_code", s.code); if (s.unit) setValue("unit", s.unit); }}
                    testid="qf-hs-code"
                  />
                  <input type="hidden" {...register("hs_code")} />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="product_description">Product description</Label>
                  <textarea id="product_description" rows={3} className={`${fieldCls} py-3 min-h-[96px]`} data-testid="qf-product-desc" {...register("product_description")} />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="specifications">Technical specifications</Label>
                  <textarea id="specifications" rows={3} className={`${fieldCls} py-3 min-h-[96px]`} placeholder="Dimensions, materials, standards, certifications, etc." data-testid="qf-specs" {...register("specifications")} />
                </div>
                <div>
                  <Label htmlFor="quantity">Quantity</Label>
                  <input id="quantity" className={fieldCls} placeholder="e.g. 500" data-testid="qf-quantity" {...register("quantity")} />
                </div>
                <div>
                  <Label htmlFor="unit">Unit of measure <span className="text-muted-foreground font-normal">(auto-set from HS code)</span></Label>
                  <input id="unit" list="pgs-units" className={fieldCls} placeholder="e.g. units (pcs)" data-testid="qf-unit" {...register("unit")} />
                  <datalist id="pgs-units">
                    {unitOptions.map((u) => <option key={u} value={u} />)}
                  </datalist>
                </div>
                <div>
                  <Label htmlFor="sourcing_country">Preferred sourcing country</Label>
                  <select id="sourcing_country" className={fieldCls} data-testid="qf-sourcing-country" defaultValue="" {...register("sourcing_country")}>
                    <option value="" disabled>Select country</option>
                    {countryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <Label htmlFor="delivery_destination">Delivery destination</Label>
                  <input id="delivery_destination" className={fieldCls} placeholder="City, country" data-testid="qf-destination" {...register("delivery_destination")} />
                </div>
                <div>
                  <Label htmlFor="timeline">Target timeline</Label>
                  <select id="timeline" className={fieldCls} data-testid="qf-timeline" defaultValue="" {...register("timeline")}>
                    <option value="" disabled>Select timeline</option>
                    {timelineOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <Label htmlFor="budget">Target budget <span className="text-muted-foreground font-normal">(optional)</span></Label>
                  <input id="budget" className={fieldCls} placeholder="e.g. USD 10,000" data-testid="qf-budget" {...register("budget")} />
                </div>
                <div>
                  <Label htmlFor="incoterm">Incoterm <span className="text-muted-foreground font-normal">(optional)</span></Label>
                  <select id="incoterm" className={fieldCls} data-testid="qf-incoterm" defaultValue="" {...register("incoterm")}>
                    <option value="" disabled>Select incoterm</option>
                    {incotermOptions.map((i) => <option key={i} value={i}>{i}</option>)}
                  </select>
                </div>
              </div>
            </fieldset>

            {/* Section 3 */}
            <fieldset>
              <legend className="font-heading font-bold text-[20px] text-navy mb-1">3. Attachments & message</legend>
              <p className="text-[14px] text-muted-foreground mb-6">Add specification sheets or drawings if you have them.</p>

              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
                className="cursor-pointer rounded-md border-2 border-dashed border-[#C3CDD3] bg-white hover:border-teal transition-colors p-8 text-center"
                data-testid="qf-dropzone"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter") fileInputRef.current?.click(); }}
              >
                <UploadCloud className="h-8 w-8 text-teal mx-auto" />
                <p className="mt-3 text-[15px] font-medium text-navy">Click to upload or drag & drop</p>
                <p className="mt-1 text-[12.5px] text-muted-foreground">PDF, DOCX, XLSX, JPG, PNG — up to 10MB each</p>
                <input ref={fileInputRef} type="file" multiple accept={ALLOWED.join(",")} className="hidden"
                  data-testid="qf-file-input"
                  onChange={(e) => handleFiles(e.target.files)} />
              </div>

              {uploading && (
                <p className="mt-3 text-[13px] text-teal flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Uploading…</p>
              )}

              {files.length > 0 && (
                <ul className="mt-4 space-y-2" data-testid="qf-file-list">
                  {files.map((f) => (
                    <li key={f.stored} className="flex items-center justify-between rounded-md border border-[#D9E0E4] bg-white px-3.5 py-2.5">
                      <span className="flex items-center gap-2.5 text-[13.5px] text-navy truncate">
                        <FileText className="h-4 w-4 text-teal shrink-0" />
                        <span className="truncate">{f.name}</span>
                        <span className="text-muted-foreground text-[12px]">({Math.round(f.size / 1024)} KB)</span>
                      </span>
                      <button type="button" onClick={() => removeFile(f.stored)} aria-label={`Remove ${f.name}`} className="text-muted-foreground hover:text-[#B42318]">
                        <X className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <div className="mt-6">
                <Label htmlFor="message">Additional message</Label>
                <textarea id="message" rows={4} className={`${fieldCls} py-3 min-h-[120px]`} data-testid="qf-message" {...register("message")} />
              </div>

              <div className="mt-6 flex items-start gap-3">
                <input id="consent" type="checkbox" className="mt-1 h-5 w-5 rounded border-[#D9E0E4] text-teal focus:ring-teal accent-[#0B6E69]"
                  data-testid="qf-consent"
                  {...register("consent", { required: "Please confirm you agree to be contacted about your enquiry." })} />
                <label htmlFor="consent" className="text-[13.5px] text-muted-foreground leading-relaxed">
                  I agree that Premier Global Sourcing may use the details I have provided to respond to this
                  enquiry, in line with the Privacy Policy. <span className="text-[#B42318]">*</span>
                </label>
              </div>
              <Err msg={errors.consent?.message} />
            </fieldset>

            {/* Error state */}
            <AnimatePresence>
              {submitError && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="rounded-md border border-[#F1C7C3] bg-[#FBEBEA] p-4"
                  data-testid="qf-error"
                  role="alert"
                >
                  <p className="text-[14px] text-[#B42318] font-medium flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" /> {submitError}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-3">
                    <button type="button" onClick={() => pendingData && onSubmit(pendingData)}
                      className="text-[13px] font-semibold text-[#B42318] underline" data-testid="qf-retry">
                      Try again
                    </button>
                    <a href={waLink()} target="_blank" rel="noopener noreferrer" className="text-[13px] font-semibold text-teal inline-flex items-center gap-1">
                      <MessageCircle className="h-3.5 w-3.5" /> Send on WhatsApp
                    </a>
                    <a href={`mailto:${contact.email}`} className="text-[13px] font-semibold text-teal inline-flex items-center gap-1">
                      <Mail className="h-3.5 w-3.5" /> Email us instead
                    </a>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <CTAButton as="button" type="submit" variant="primary" loading={submitting} disabled={submitting}
                icon={submitting ? undefined : ArrowRight} data-testid="qf-submit">
                {submitting ? "Submitting…" : "Submit your requirement"}
              </CTAButton>
              <p className="text-[12.5px] text-muted-foreground">
                Protected by spam filtering. We respond to genuine business enquiries.
              </p>
            </div>
          </form>
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-4">
          <div className="sticky top-24 space-y-5">
            <div className="rounded-lg border border-[#D9E0E4] bg-white p-6">
              <h2 className="font-heading font-bold text-[17px] text-navy">Prefer to talk first?</h2>
              <p className="mt-2 text-[14px] text-muted-foreground">Reach us directly and we will guide you on what to send.</p>
              <div className="mt-5 space-y-3">
                <CTAButton href={waLink()} target="_blank" rel="noopener noreferrer" variant="primary" icon={MessageCircle} className="w-full" data-testid="qf-side-whatsapp">
                  Ask on WhatsApp
                </CTAButton>
                <CTAButton href={contact.phoneHref} variant="outline" className="w-full" data-testid="qf-side-call">
                  Call {contact.phone}
                </CTAButton>
              </div>
            </div>
            <div className="rounded-lg bg-alt border border-[#D9E0E4] p-6">
              <p className="text-[13px] font-semibold uppercase tracking-wide text-teal">What happens next</p>
              <ol className="mt-4 space-y-3 text-[13.5px] text-muted-foreground">
                <li className="flex gap-3"><span className="font-heading font-bold text-copper">1.</span> We review your requirement.</li>
                <li className="flex gap-3"><span className="font-heading font-bold text-copper">2.</span> We confirm any details needed.</li>
                <li className="flex gap-3"><span className="font-heading font-bold text-copper">3.</span> We research suitable options.</li>
                <li className="flex gap-3"><span className="font-heading font-bold text-copper">4.</span> We explain the next steps.</li>
              </ol>
            </div>
          </div>
        </aside>
      </section>
    </>
  );
}
